'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { alpha } from '@mantine/core';

// UI local components
import getFileIcon from './getFileIcon';

// Types
import { ResourceIconProps } from './types';

// Assets
import {
  IconFolder,
  IconFolderOpen as IconFolderExpanded,
  IconFolderDown,
  IconFolderShare,
  IconFileUnknown,
} from '@tabler/icons-react';

// Style
import { palette } from '@ui-kit/colors';
import { ResourceMimeType } from '@app-types/blackroom-app/resourceMimeType';
import * as classes from './styles/index.css';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ResourceIcon({
  resource,
  variant = resource?.type === 'folder' ? 'filled' : 'outlined',
  withColor,
  folderExpanded,
  systemFolderType,
  ...passedProps
}: ResourceIconProps) {
  /* ********************************** PROPS ********************************* */

  const props = { ...passedProps };

  /* ********************************** HOOKS ********************************* */

  if (variant === 'filled') {
    props.fill = props.color;
    props.color = palette.blackroomWhite;
  }

  /* ******************************** RENDERING ******************************* */

  if (!resource) {
    return null;
  }

  if (resource.type === 'folder') {
    if (systemFolderType === 'dump') {
      return (
        <IconFolderDown
          strokeWidth={1.5}
          color={palette.blackroomBlue}
          fill={alpha(palette.blackroomBlue, 0.15)}
        />
      );
    }
    if (systemFolderType === 'dataroom') {
      return (
        <IconFolderShare
          strokeWidth={1.5}
          color={palette.blackroomViolet}
          fill={alpha(palette.blackroomViolet, 0.15)}
        />
      );
    }
    return folderExpanded ? (
      <IconFolderExpanded strokeWidth={1.5} color="white" fill="black" />
    ) : (
      <IconFolder {...props} />
    );
  }

  if (!resource.mimeType) {
    return <IconFileUnknown {...props} />;
  }

  const FileIcon = getFileIcon(resource.mimeType! as ResourceMimeType);

  return (
    <FileIcon.Component
      className={classes.icon}
      {...(withColor
        ? {
            color: FileIcon.color,
            style: { backgroundColor: alpha(FileIcon.color, 0.2) },
          }
        : {})}
      {...props}
    />
  );
}

export default ResourceIcon;
